import { Box, Flex, Grid, IconButton, Input, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { HiPlus, HiXMark } from 'react-icons/hi2';

export interface CommandTemplateField {
  key: string;
  value: string;
}

export interface CommandTemplate {
  name: string;
  fields: CommandTemplateField[];
}

interface CommandTemplateBuilderProps {
  templates: CommandTemplate[];
  onChange: (templates: CommandTemplate[]) => void;
}

/**
 * Guided builder for PBX/AMI command templates.
 *
 * Each template is a named command (e.g. "default_alert", "active_shooter")
 * containing key-value fields that become telnet packet lines.
 */
const CommandTemplateBuilder = ({
  templates,
  onChange,
}: CommandTemplateBuilderProps) => {
  const { t } = useTranslation();
  const updateTemplate = (
    idx: number,
    update: Partial<CommandTemplate>,
  ) => {
    const next = templates.map((t, i) =>
      i === idx ? { ...t, ...update } : t,
    );
    onChange(next);
  };

  const removeTemplate = (idx: number) => {
    onChange(templates.filter((_, i) => i !== idx));
  };

  const addTemplate = () => {
    onChange([
      ...templates,
      { name: '', fields: [{ key: '', value: '' }] },
    ]);
  };

  const updateField = (
    tIdx: number,
    fIdx: number,
    update: Partial<CommandTemplateField>,
  ) => {
    const next = templates.map((t, i) => {
      if (i !== tIdx) return t;
      return {
        ...t,
        fields: t.fields.map((f, j) =>
          j === fIdx ? { ...f, ...update } : f,
        ),
      };
    });
    onChange(next);
  };

  const removeField = (tIdx: number, fIdx: number) => {
    const next = templates.map((t, i) => {
      if (i !== tIdx) return t;
      return { ...t, fields: t.fields.filter((_, j) => j !== fIdx) };
    });
    onChange(next);
  };

  const addField = (tIdx: number) => {
    const next = templates.map((t, i) => {
      if (i !== tIdx) return t;
      return { ...t, fields: [...t.fields, { key: '', value: '' }] };
    });
    onChange(next);
  };

  return (
    <Grid gap="12px">
      {templates.map((template, tIdx) => (
        <Box
          key={tIdx}
          border="1px solid"
          borderColor="border.secondary"
          borderRadius="lg"
          p="12px"
          data-e2e={`command-template-${tIdx}`}
        >
          <Flex justify="space-between" align="center" mb="8px">
            <Input
              placeholder={t('command_name_placeholder')}
              value={template.name}
              onChange={(e) =>
                updateTemplate(tIdx, { name: e.target.value })
              }
              size="sm"
              fontWeight={600}
              fontSize="13px"
              bg="surface.primary"
              color="text.secondary"
              borderColor="border.secondary"
              borderRadius="md"
              flex={1}
              mr="8px"
              data-e2e={`command-template-name-${tIdx}`}
            />
            <IconButton
              aria-label={t('remove_command')}
              size="xs"
              variant="ghost"
              color="text.tertiary"
              onClick={() => removeTemplate(tIdx)}
              data-e2e={`command-template-remove-${tIdx}`}
            >
              <HiXMark />
            </IconButton>
          </Flex>

          <Grid gap="6px">
            {template.fields.map((field, fIdx) => (
              <Flex key={fIdx} gap="6px" align="center">
                <Input
                  placeholder={t('key')}
                  value={field.key}
                  onChange={(e) =>
                    updateField(tIdx, fIdx, { key: e.target.value })
                  }
                  size="sm"
                  fontSize="13px"
                  bg="surface.primary"
                  color="text.secondary"
                  borderColor="border.secondary"
                  borderRadius="md"
                  flex={1}
                  data-e2e={`command-field-key-${tIdx}-${fIdx}`}
                />
                <Input
                  placeholder={t('value')}
                  value={field.value}
                  onChange={(e) =>
                    updateField(tIdx, fIdx, { value: e.target.value })
                  }
                  size="sm"
                  fontSize="13px"
                  bg="surface.primary"
                  color="text.secondary"
                  borderColor="border.secondary"
                  borderRadius="md"
                  flex={1}
                  data-e2e={`command-field-value-${tIdx}-${fIdx}`}
                />
                <IconButton
                  aria-label={t('remove_field')}
                  size="xs"
                  variant="ghost"
                  color="text.tertiary"
                  onClick={() => removeField(tIdx, fIdx)}
                  data-e2e={`command-field-remove-${tIdx}-${fIdx}`}
                >
                  <HiXMark />
                </IconButton>
              </Flex>
            ))}
          </Grid>

          <Flex
            as="button"
            align="center"
            gap="4px"
            mt="8px"
            px="8px"
            py="4px"
            fontSize="12px"
            color="text.tertiary"
            cursor="pointer"
            borderRadius="md"
            _hover={{ color: 'text.secondary', bg: 'surface.tertiary' }}
            onClick={() => addField(tIdx)}
            data-e2e={`command-field-add-${tIdx}`}
          >
            <HiPlus size={12} />
            {t('add_field')}
          </Flex>
        </Box>
      ))}

      <Flex
        as="button"
        align="center"
        justify="center"
        gap="6px"
        p="10px"
        border="1px dashed"
        borderColor="border.secondary"
        borderRadius="lg"
        cursor="pointer"
        fontSize="13px"
        color="text.tertiary"
        _hover={{ borderColor: 'border.active', color: 'text.secondary' }}
        onClick={addTemplate}
        data-e2e="command-template-add"
      >
        <HiPlus size={14} />
        {t('add_command_template')}
      </Flex>
    </Grid>
  );
};

export default CommandTemplateBuilder;
